import { TeamDescendantsTree } from '@internal/plugin-techinsights-module-entities';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useAsync } from 'react-use';

import {
  useApi,
  identityApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';

import { CategoryData } from '../../types';
import { toStandardISO } from '../../utils';

const normalizeTeamTree = (
  teamTree?: TeamDescendantsTree | TeamDescendantsTree[],
): TeamDescendantsTree[] => {
  if (!teamTree) {
    return [];
  }
  if (!Array.isArray(teamTree)) {
    return [teamTree];
  }
  if (!teamTree.length || !teamTree[0]) {
    return [];
  }
  return teamTree;
};

export const useGetCategoriesData = (
  teamTree?: TeamDescendantsTree | TeamDescendantsTree[],
  startDate?: string,
  endDate?: string,
  aggregateBy = 'month',
  refreshKey?: number,
) => {
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const teamTreeIds = useMemo(() => {
    const toArrayTree = normalizeTeamTree(teamTree);
    return JSON.stringify(toArrayTree);
  }, [teamTree]);

  const { value: categoriesData, loading } = useAsync(async () => {
    const toArrayTree = JSON.parse(teamTreeIds);

    if (!toArrayTree.length) {
      return null;
    }

    const reqBody = {
      team_ids: toArrayTree,
      start_date:
        startDate ??
        toStandardISO(DateTime.now().minus({ months: 5 }).startOf('month')),
      end_date: endDate ?? toStandardISO(DateTime.now().endOf('month')),
      aggregate_by: aggregateBy,
    };

    try {
      const { token } = await identityApi.getCredentials();
      const endpointBase = await discoveryApi.getBaseUrl('watchtower');

      const response = await fetch(`${endpointBase}/categories-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reqBody),
      });
      const data = await response.json();
      return data;
    } catch (_error) {
      return null;
    }
  }, [teamTree, startDate, endDate, aggregateBy, refreshKey]);

  return {
    value:
      categoriesData?.filter((cData: CategoryData) => cData !== null) ?? null,
    loading,
  };
};
